import { onCleanup, onMount } from 'solid-js';
import { isServer } from 'solid-js/web';

const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SCRIPT_POLL_INTERVAL_MS = 50;
const SCRIPT_LOAD_TIMEOUT_MS = 10000;

type TurnstileProps = {
    siteKey: string;
    onVerify: (token: string) => void;
    onExpire?: () => void;
    onError?: () => void;
    // Fired when the CF script never becomes available (blocked by an ad
    // blocker, network policy, or an outage). Lets the form explain why it
    // can't be submitted instead of demanding a widget that isn't there.
    onUnavailable?: () => void;
    theme?: 'light' | 'dark' | 'auto';
    language?: string;
    // Hands a reset() back to the parent so it can clear the widget after a
    // submit (tokens are single-use) or after a failed verification.
    registerReset?: (reset: () => void) => void;
};

/**
 * Renders a Cloudflare Turnstile widget. Client-only: the container is emitted
 * during SSR but the widget itself is created in onMount, after the CF script
 * has loaded. See docs/turnstile-captcha-plan.md.
 */
export const Turnstile = (props: TurnstileProps) => {
    let container: HTMLDivElement | undefined;
    let widgetId: string | undefined;

    const ensureScript = (onLoadError: () => void) => {
        if (document.getElementById(SCRIPT_ID)) {
            return;
        }
        const script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.src = SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        // Blocked requests reject here, which is much faster than waiting out
        // the poll timeout below.
        script.addEventListener('error', onLoadError);
        document.head.appendChild(script);
    };

    const renderWidget = () => {
        if (!container || !window.turnstile?.render) {
            return;
        }
        widgetId = window.turnstile.render(container, {
            sitekey: props.siteKey,
            theme: props.theme ?? 'auto',
            language: props.language,
            callback: (token: string) => props.onVerify(token),
            'expired-callback': () => {
                props.onVerify('');
                props.onExpire?.();
            },
            'error-callback': () => {
                props.onVerify('');
                props.onError?.();
            },
        });

        props.registerReset?.(() => {
            if (widgetId && window.turnstile?.reset) {
                window.turnstile.reset(widgetId);
                props.onVerify('');
            }
        });
    };

    onMount(() => {
        if (isServer || !props.siteKey) {
            return;
        }

        let settled = false;
        // Declared before the timers so giveUp can clear them, and assigned
        // after the early return below — hence `let` with an explicit initial
        // value rather than `const`.
        let interval: ReturnType<typeof setInterval> | undefined = undefined;
        let timeout: ReturnType<typeof setTimeout> | undefined = undefined;

        const stopWaiting = () => {
            settled = true;
            clearInterval(interval);
            clearTimeout(timeout);
        };

        const giveUp = () => {
            if (settled) {
                return;
            }
            stopWaiting();
            props.onUnavailable?.();
        };

        ensureScript(giveUp);

        if (window.turnstile?.render) {
            renderWidget();
            return;
        }

        // The script loads asynchronously; poll until the API is ready.
        interval = setInterval(() => {
            if (window.turnstile?.render) {
                stopWaiting();
                renderWidget();
            }
        }, SCRIPT_POLL_INTERVAL_MS);
        timeout = setTimeout(giveUp, SCRIPT_LOAD_TIMEOUT_MS);

        onCleanup(stopWaiting);
    });

    onCleanup(() => {
        if (isServer) {
            return;
        }
        if (widgetId && window.turnstile?.remove) {
            window.turnstile.remove(widgetId);
        }
    });

    return <div ref={container} class="cf-turnstile-container" />;
};
